/**
 * Empresas del estudio — gestión de la cartera de clientes (firm_admin).
 *
 * Lista las empresas cliente, permite asignar un contador responsable y
 * activar/desactivar cada empresa, y abre la provisión masiva de acceso. La
 * creación de empresas la hace Zenlatam (Opción A) → desde aquí se "Solicita".
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2, Search, UserPlus, Users, Power, PlusCircle, ExternalLink,
} from 'lucide-react'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { Badge, StatusBadge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { KebabMenu } from '@/components/KebabMenu'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { DataTable } from '@/components/ds/DataTable'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { ToastContainer, useToast } from '@/components/Toast'
import { getApiErrorMessage } from '@/lib/apiClient'
import {
  useFirmClients, useFirmStaff, useUpdateFirmClient, type FirmClient,
} from '@/modules/firm/hooks/useFirm'
import { ProvisionModal } from './ProvisionModal'
import { useOpenTenant } from './firmShared'

const fieldCls =
  'w-full px-3 py-2 text-sm rounded-lg bg-surface border border-divider text-content focus:outline-none focus:ring-2 focus:ring-focus-ring'

export function FirmClientsPage() {
  const { data: clients, isLoading } = useFirmClients()
  const { data: staff } = useFirmStaff()
  const updateClient = useUpdateFirmClient()
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const { openTenant, canOpen } = useOpenTenant()

  const [search, setSearch] = useState('')
  const [provisionOpen, setProvisionOpen] = useState(false)
  const [assignFor, setAssignFor] = useState<FirmClient | null>(null)
  const [assignUserId, setAssignUserId] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = clients ?? []
    if (!q) return list
    return list.filter(
      (c) => c.tenant_name.toLowerCase().includes(q) || c.tenant_schema.toLowerCase().includes(q),
    )
  }, [clients, search])

  const openAssign = (c: FirmClient) => {
    setAssignFor(c)
    setAssignUserId(c.assigned_to ?? '')
  }

  const submitAssign = async () => {
    if (!assignFor) return
    try {
      await updateClient.mutateAsync({
        id: assignFor.id,
        payload: { assigned_to: assignUserId || null },
      })
      toast.success('Contador actualizado.')
      setAssignFor(null)
    } catch (e) {
      toast.error(getApiErrorMessage(e))
    }
  }

  const toggleActive = (c: FirmClient) => {
    confirm({
      message: c.is_active
        ? `¿Desactivar a ${c.tenant_name}? Dejará de aparecer en la cartera.`
        : `¿Reactivar a ${c.tenant_name}?`,
      variant: c.is_active ? 'danger' : 'success',
      confirmLabel: c.is_active ? 'Desactivar' : 'Reactivar',
      onConfirm: async () => {
        try {
          await updateClient.mutateAsync({ id: c.id, payload: { is_active: !c.is_active } })
          toast.success(c.is_active ? 'Empresa desactivada.' : 'Empresa reactivada.')
        } catch (e) {
          toast.error(getApiErrorMessage(e))
        }
      },
    })
  }

  return (
    <div className="p-4 sm:p-6">
      <PageHeader
        title="Empresas"
        subtitle="Cartera de empresas cliente del estudio."
        help={HELP['firm.clients']}
      >
        <Link
          to="/firm/requests"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-divider rounded-lg hover:bg-soft transition-colors"
        >
          <PlusCircle size={14} />
          Solicitar empresa
        </Link>
        <Button onClick={() => setProvisionOpen(true)} variant="primary">
          <Users size={15} /> Provisión masiva
        </Button>
      </PageHeader>

      <div className="relative mb-3 max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
        <input aria-label="Buscar empresa"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar empresa…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-surface border border-divider text-content placeholder:text-content-subtle focus:outline-none focus:ring-2 focus:ring-focus-ring"
        />
      </div>

      <DataTable
        rows={filtered}
        rowKey={c => c.id}
        loading={isLoading}
        loadingNode={<TableSkeleton rows={5} cols={4} />}
        minWidth={760}
        caption="Empresas cliente del estudio"
        empty={
          <EmptyState icon={Building2} title="Sin empresas"
            hint={search ? 'Ninguna empresa coincide con la búsqueda.' : 'El estudio aún no tiene empresas cliente.'} />
        }
        columns={[
          {
            key: 'tenant_name', header: 'Empresa', sortable: true,
            render: c => (
              <>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-content">{c.tenant_name}</span>
                  {c.is_own_company && <Badge label="Tu estudio" variant="indigo" />}
                </div>
                <span className="block text-xs text-content-subtle">{c.tenant_schema}</span>
              </>
            ),
          },
          {
            key: 'assigned_to', header: 'Contador asignado', sortable: true,
            // Sin asignar va como VACIO y no como el texto: asi el componente
            // lo manda al final del orden en vez de alfabetizarlo por la S.
            sortValue: c => c.assigned_to_detail?.full_name ?? null,
            render: c => (
              <span className="text-content-muted">
                {c.assigned_to_detail?.full_name || <span className="text-content-subtle">Sin asignar</span>}
              </span>
            ),
          },
          {
            key: 'is_active', header: 'Estado', sortable: true, width: 110,
            sortValue: c => (c.is_active ? 1 : 0),
            render: c => <StatusBadge active={c.is_active} />,
          },
          {
            key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 60,
            render: c => (
              <div className="text-right">
                <KebabMenu items={[
                  {
                    label: 'Abrir empresa',
                    icon: <ExternalLink size={14} />,
                    onClick: () => openTenant(c.tenant_schema),
                    hidden: !canOpen(c.tenant_schema),
                  },
                  {
                    label: 'Asignar contador',
                    icon: <UserPlus size={14} />,
                    onClick: () => openAssign(c),
                  },
                  {
                    label: c.is_active ? 'Desactivar' : 'Reactivar',
                    icon: <Power size={14} />,
                    onClick: () => toggleActive(c),
                    className: c.is_active ? 'text-danger-content-soft hover:bg-danger-surface' : undefined,
                  },
                ]} />
              </div>
            ),
          },
        ]}
      />

      {/* Modal asignar contador */}
      <Modal
        open={assignFor !== null}
        onClose={() => setAssignFor(null)}
        title={`Asignar contador — ${assignFor?.tenant_name ?? ''}`}
        size="sm"
        actions={
          <>
            <ModalCancelButton onClick={() => setAssignFor(null)} />
            <Button onClick={submitAssign} isPending={updateClient.isPending} variant="primary" size="lg">
              Guardar
            </Button>
          </>
        }
      >
        <div className="">
          <label htmlFor="firmclientspage-contador-responsable" className="block text-sm font-medium text-content mb-1">Contador responsable</label>
          <select id="firmclientspage-contador-responsable" value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)} className={fieldCls}>
            <option value="">Sin asignar</option>
            {(staff ?? []).map((m) => (
              <option key={m.id} value={m.user}>
                {m.user_detail.full_name || m.user_detail.email}
              </option>
            ))}
          </select>
          <p className="text-xs text-content-subtle mt-2">
            El contador asignado verá esta empresa en su cartera. Para que pueda operar dentro
            de la empresa, otórgale acceso con la provisión masiva.
          </p>
        </div>
      </Modal>

      <ProvisionModal open={provisionOpen} onClose={() => setProvisionOpen(false)} toast={toast} />
      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

export default FirmClientsPage
